import Link from "next/link";
import { useOffer } from "@nju/data-access-portal-offers";
import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import {
  IAccess,
  IInternet,
  IModem,
  IServiceStatus,
  ISfh,
} from "@nju/data-access-portal-product";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, CircleLoader, Panel, Separator, Text } from "@nju/ui";
import { formatDate, formatPrice } from "@nju/utils";
import { Row } from "./partials/Row";
import { Title } from "./partials/Title";

type IStates = "IDLE" | "LOADING" | "ERROR" | "DONE";

interface IFtthOrderCompletedStatusProps {
  sfh: ISfh | undefined;
  modem: IModem | undefined;
  internet: IInternet | undefined;
  orchestrator: IFtthOrchestrator;
  ftthServiceStatus: IServiceStatus["ftthServiceStatus"];
  discount: string;
  currentState: IStates;
  access: IAccess | undefined;
}

export function FtthOrderCompletedStatus({
  sfh,
  modem,
  internet,
  orchestrator,
  ftthServiceStatus,
  discount,
  currentState,
  access,
}: IFtthOrderCompletedStatusProps) {
  const isActive = ftthServiceStatus === "Active";
  const isPendingActive = ftthServiceStatus === "PendingActive";
  const { paymentTimestamp, orderSummary } = orchestrator;
  const offer = useOffer(access?.vlocityOfferId);
  const shouldShowDiscountInfo =
    Number(internet?.balances?.[0]?.currentValue) > 0;

  // required to present properly component, comes from products
  if (!modem && !internet) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  return (
    <>
      {ftthServiceStatus !== "PendingCancellation" && (
        <div className="grid gap-4 from-desktop:grid-cols-2">
          {offer?.category === "FTTH" && (
            <Panel testId="speed-tab">
              <Title iconName="tachometer-alt-fastest" title="Twoja prędkość" />
              <Separator size="s" />
              {offer?.downloadFullText && (
                <Row
                  title="prędkość pobierania:"
                  value={`do ${offer?.downloadFullText}`}
                />
              )}
              {offer?.uploadFullText && (
                <Row
                  title="prędkość wysyłania:"
                  value={`do ${offer?.uploadFullText}`}
                />
              )}
            </Panel>
          )}
          <Panel testId="device-details-tab">
            <Title iconName="router" title="dane urządzenia" />
            <Separator size="s" />
            {modem?.offeringName && (
              <div className="flex flex-col">
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "l",
                  }}
                >
                  modem:
                </Text>
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "l",
                  }}
                  weight="bold"
                >
                  bezpłatny {modem.offeringName} z Wi-Fi
                </Text>
              </div>
            )}
            {modem && (
              <div className="flex flex-col">
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "l",
                  }}
                >
                  numer seryjny:
                </Text>
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "l",
                  }}
                  weight="bold"
                >
                  {modem.characteristic.serialNumber}
                </Text>
              </div>
            )}
          </Panel>
          {currentState === "LOADING" ? (
            <div className="flex justify-center">
              <CircleLoader />
            </div>
          ) : (
            <Panel testId="ftth-payment-tab">
              <Title iconName="wallet" title="najbliższa płatność" />
              <Separator size="s" />
              {/* jeśli jesteśmy w stanie produktów "Active" lub "PendingActive", wyświetlamy datę cyklicznej lub pierwszej płatności*/}
              {/* jeśli jesteśmy w innym stanie, wyświetlamy tekst "opłata czasowo zawieszona" */}
              {isActive || isPendingActive ? (
                internet?.anniversaryDate ? (
                  <Text
                    size={{
                      "@onlyMobile": "s",
                      "@fromTablet": "m",
                    }}
                    tone="accent"
                  >
                    {formatDate(internet.anniversaryDate, false)}
                  </Text>
                ) : (
                  (paymentTimestamp && (
                    <Text
                      size={{
                        "@onlyMobile": "s",
                        "@fromTablet": "m",
                      }}
                      tone="accent"
                    >
                      {formatDate(paymentTimestamp, false)}
                    </Text>
                  )) ||
                  // eslint-disable-next-line unicorn/no-null
                  null
                )
              ) : (
                <Text
                  size={{
                    "@onlyMobile": "s",
                    "@fromTablet": "m",
                  }}
                  tone="accent"
                >
                  opłata czasowo zawieszona
                </Text>
              )}
              {/* sprawdzamy czy opłata jest pierwsza, lub cykliczna */}
              {internet?.anniversaryDate
                ? // we need to check explicitly if recurringPrice is undefined, because we accept 0 numeric value
                  (internet?.recurringPrice !== undefined && (
                    <Row
                      title="opłata nju światłowód"
                      value={`${
                        sfh?.recurringPrice
                          ? formatPrice(
                              internet.recurringPrice + sfh.recurringPrice,
                            )
                          : formatPrice(internet.recurringPrice)
                      } PLN`}
                    />
                  )) ||
                  // eslint-disable-next-line unicorn/no-null
                  null
                : (orderSummary?.totalOrderPrice && (
                    <Row
                      title="pierwsza opłata nju światłowód "
                      value={`${
                        sfh?.recurringPrice
                          ? formatPrice(
                              orderSummary.totalOrderPrice + sfh.recurringPrice,
                            )
                          : formatPrice(orderSummary.totalOrderPrice)
                      } PLN`}
                    />
                  )) ||
                  // eslint-disable-next-line unicorn/no-null
                  null}
              {discount && shouldShowDiscountInfo && (
                <>
                  <Separator size="xs" tone="invisible" />
                  <div className="flex items-start from-tablet:items-center">
                    <Icon
                      className="mr-2"
                      height={24}
                      name="info-circle"
                      tone="accent"
                      width={24}
                    />
                    <Text
                      size={{
                        "@onlyMobile": "s",
                        "@fromTablet": "m",
                      }}
                      tone="accent"
                    >
                      {discount}
                    </Text>
                  </div>
                </>
              )}
              {isActive && (
                <>
                  <Separator size="xs" tone="invisible" />
                  <div className="flex items-start from-tablet:items-center">
                    <Icon
                      className="mr-2"
                      height={24}
                      name="sync"
                      tone="accent"
                      width={24}
                    />
                    <Text
                      size={{
                        "@onlyMobile": "s",
                        "@fromTablet": "m",
                      }}
                    >
                      usługa odnawia się automatycznie co miesiąc
                    </Text>
                  </div>
                </>
              )}
            </Panel>
          )}
          <Panel testId="modem-configuration-tab">
            <Title iconName="cog" title="konfiguracja modemu" />
            <Separator size="s" />
            {internet?.characteristic.login && (
              <Row title="login:" value={internet.characteristic.login} />
            )}
            <div className="flex justify-between">
              <Text
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
              >
                hasło:
              </Text>
              <Text
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                <Link
                  href={portalRoutes.mojeKonto.wygenerujNoweHaslo.get()}
                  passHref={true}
                >
                  <A display="inline" size="inline" variant="simple">
                    <Text tone="accent" weight="bold" withUnderline={true}>
                      wygeneruj nowe hasło
                    </Text>
                  </A>
                </Link>
              </Text>
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}
